import React, { useState } from 'react';
import { Card, Button, Upload, message, Space, Row, Col, Tabs, Alert, Spin } from 'antd';
import { FileOutlined, SwapOutlined, EyeOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import api from '../services/api';

const { TabPane } = Tabs;

interface ContractFile {
  id: string;
  title: string;
  upload_time: string;
}

const ComparePage: React.FC = () => {
  const [originalFile, setOriginalFile] = useState<ContractFile | null>(null);
  const [modifiedFile, setModifiedFile] = useState<ContractFile | null>(null);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('diff');

  const uploadProps = (type: 'original' | 'modified'): UploadProps => ({
    name: 'file',
    accept: '.docx,.doc',
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await api.post('/upload', formData);
        if (type === 'original') {
          setOriginalFile(response.data);
        } else {
          setModifiedFile(response.data);
        }
        message.success(`${(file as File).name} 上传成功`);
        onSuccess?.(response.data);
      } catch (error) {
        message.error('上传失败');
        onError?.(error as Error);
      }
    },
  });

  const handleCompare = async () => {
    if (!originalFile || !modifiedFile) {
      message.warning('请上传两个合同文件');
      return;
    }
    
    setComparing(true);
    try {
      const response = await api.post('/compare', {
        original_contract_id: originalFile.id,
        modified_contract_id: modifiedFile.id
      });
      setResult(response.data.result);
      setActiveTab('summary');
      message.success('比对完成');
    } catch (error: any) {
      console.error('比对失败:', error);
      message.error(error.response?.data?.detail || '比对失败');
    } finally {
      setComparing(false);
    }
  };

  const clearFiles = () => {
    setOriginalFile(null);
    setModifiedFile(null);
    setResult(null);
  };

  return (
    <div>
      <h1 style={{ marginBottom: 8 }}>合同比对审查</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>
        上传原版和修改版合同，系统会自动对比差异并审查改动部分的风险
      </p>
      
      <Row gutter={24}>
        <Col span={12}>
          <Card 
            title="原版合同" 
            extra={originalFile && <Button type="link" onClick={() => setOriginalFile(null)}>重新上传</Button>}
          >
            {!originalFile ? (
              <Upload.Dragger {...uploadProps('original')}>
                <p className="ant-upload-drag-icon"><FileOutlined /></p>
                <p>点击或拖拽上传原版合同</p>
                <p style={{ color: '#999' }}>支持 .docx 格式</p>
              </Upload.Dragger>
            ) : (
              <Alert 
                message={originalFile.title} 
                description={`上传时间: ${new Date(originalFile.upload_time).toLocaleString()}`}
                type="success" 
                showIcon 
              />
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card 
            title="修改版合同" 
            extra={modifiedFile && <Button type="link" onClick={() => setModifiedFile(null)}>重新上传</Button>}
          >
            {!modifiedFile ? (
              <Upload.Dragger {...uploadProps('modified')}>
                <p className="ant-upload-drag-icon"><FileOutlined /></p>
                <p>点击或拖拽上传修改版合同</p>
                <p style={{ color: '#999' }}>支持 .docx 格式</p>
              </Upload.Dragger>
            ) : (
              <Alert 
                message={modifiedFile.title} 
                description={`上传时间: ${new Date(modifiedFile.upload_time).toLocaleString()}`}
                type="success" 
                showIcon 
              />
            )}
          </Card>
        </Col>
      </Row>
      
      <div style={{ textAlign: 'center', margin: '32px 0' }}>
        <Space size="large">
          <Button 
            type="primary" 
            size="large" 
            icon={<SwapOutlined />}
            onClick={handleCompare}
            loading={comparing}
            disabled={!originalFile || !modifiedFile}
          >
            开始比对
          </Button>
          {(originalFile || modifiedFile) && (
            <Button size="large" onClick={clearFiles}>
              清空文件
            </Button>
          )}
        </Space>
      </div>
      
      {comparing && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin tip="AI正在分析合同差异..." />
        </div>
      )}
      
      {result && (
        <Card title="比对结果">
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="差异摘要" key="summary">
              <Row gutter={16}>
                <Col span={8}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 32, color: '#1890ff' }}>{result.diff.total_changes}</div>
                      <div>总改动处</div>
                    </div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 32, color: '#52c41a' }}>{result.diff.added_lines.length}</div>
                      <div>新增行</div>
                    </div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 32, color: '#ff4d4f' }}>{result.diff.removed_lines.length}</div>
                      <div>删除行</div>
                    </div>
                  </Card>
                </Col>
              </Row>
              <div style={{ marginTop: 16 }}>
                <h4>修改段落：</h4>
                {result.diff.modified_sections.map((section: any, idx: number) => (
                  <Card key={idx} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                    <div><strong>原内容：</strong>{section.original.substring(0, 200)}...</div>
                    <div style={{ marginTop: 8 }}><strong>新内容：</strong>{section.modified.substring(0, 200)}...</div>
                  </Card>
                ))}
              </div>
            </TabPane>
            <TabPane tab="风险审查" key="risk">
              {result.audit_results.length === 0 ? (
                <Alert message="未发现需要审查的风险改动" type="info" />
              ) : (
                result.audit_results.map((item: any, idx: number) => (
                  <Card key={idx} style={{ marginBottom: 16 }} title={`修改段落 ${idx + 1}`}>
                    <div style={{ marginBottom: 12 }}>
                      <div><strong>原内容：</strong></div>
                      <div style={{ background: '#fff2e8', padding: 8, borderRadius: 4 }}>
                        {item.original.substring(0, 300)}
                        {item.original.length > 300 && '...'}
                      </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <div><strong>新内容：</strong></div>
                      <div style={{ background: '#f6ffed', padding: 8, borderRadius: 4 }}>
                        {item.modified.substring(0, 300)}
                        {item.modified.length > 300 && '...'}
                      </div>
                    </div>
                    <Alert
                      message={item.audit.summary || 'AI审查结果'}
                      description={
                        <div>
                          {item.audit.risk_items?.map((risk: any, rIdx: number) => (
                            <div key={rIdx} style={{ marginTop: 8 }}>
                              <strong>{risk.type}</strong>: {risk.description}
                              <br />
                              <span style={{ color: '#fa8c16' }}>建议：</span>{risk.suggestion}
                            </div>
                          ))}
                        </div>
                      }
                      type={item.audit.risk_items?.some((r: any) => r.risk_level === 'high') ? 'error' : 'warning'}
                      showIcon
                    />
                  </Card>
                ))
              )}
            </TabPane>
          </Tabs>
        </Card>
      )}
    </div>
  );
};

export default ComparePage;